import { useCallback, useMemo, useState } from 'react'
import {
    ColumnDef,
    getCoreRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
    getPaginationRowModel,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import {
    EmissionValue,
    GHGEmissionsReportEntry,
    GHGEmissionsReportSettings,
} from '@/hooks'
import { usePaginationConfig } from '@/hooks/usePaginationConfig/usePaginationConfig'

import { formatNumber } from '@/utils/formatValueWithUnit'
import { GHGEmissionsTable } from './GHGEmissionsTable'
import { formatDateRangeForTable } from '@/utils/formatDateForTable'
import { EmissionScopeKey, getEmissionScopes } from './getEmissionScopes'
import { getUnitSymbol } from '@/hooks/useApi/utils/processUsageCostResponse'

export type GHGEmissionsTableContainerProps = {
    data: GHGEmissionsReportEntry[]
    isLoading?: boolean
    startDate?: string
    endDate?: string
    reportSettings: GHGEmissionsReportSettings
}

export const GHGEmissionsTableContainer: React.FC<
    GHGEmissionsTableContainerProps
> = ({ data, isLoading = false, startDate, endDate, reportSettings }) => {
    const { t } = useTranslation('translations')
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'facilityName', desc: false },
    ])
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    })
    const dateRange =
        startDate && endDate ? formatDateRangeForTable(startDate, endDate) : ''

    const getEmissionValue = useCallback(
        (emissions: EmissionValue[], emissionType: string) => {
            if (emissions.length === 0) return 'N/A'
            const emissionsFound = emissions.find(
                (em) => em.type === emissionType
            )
            return emissionsFound ? formatNumber(emissionsFound.value) : 'N/A'
        },
        []
    )

    const getEmissionNumericValue = useCallback(
        (emissions: EmissionValue[], emissionType: string): number => {
            const emissionsFound = emissions.find(
                (em) => em.type === emissionType
            )
            return emissionsFound?.value ?? 0
        },
        []
    )

    const emissionScopes = useMemo(
        () => getEmissionScopes(reportSettings, t),
        [reportSettings, t]
    )

    const columns = useMemo<ColumnDef<GHGEmissionsReportEntry>[]>(() => {
        const baseColumns: ColumnDef<GHGEmissionsReportEntry>[] = [
            {
                id: 'facilityName',
                accessorKey: 'facilityName',
                header: t('sustainability.table.facility_name'),
                enableSorting: true,
                sortingFn: 'basic',
                cell: ({ cell }) => cell.getValue() || 'N/A',
            },
            {
                id: 'commodityType',
                accessorKey: 'commodityType',
                header: t('sustainability.table.commodity_type'),
                enableSorting: true,
                sortingFn: 'basic',
                cell: ({ cell, row }) => {
                    const commodityType =
                        (cell.getValue() as string | undefined) || ''
                    const uom = row.original.uom
                    if (!commodityType && !uom) return 'N/A'
                    return `${commodityType} (${getUnitSymbol(uom)})`
                },
            },
            {
                id: 'consumption',
                accessorKey: 'consumption',
                header: t('sustainability.table.consumption'),
                enableSorting: true,
                sortingFn: 'basic',
                cell: ({ cell }) =>
                    typeof cell.getValue() === 'number'
                        ? formatNumber(cell.getValue() as number)
                        : 'N/A',
            },
        ]

        const emissionTypes = [
            {
                type: 'CO2EEF',
                header: t('sustainability.table.co2eef_kg_uom'),
            },
            {
                type: 'CO2E',
                header: t('sustainability.table.co2e_mt'),
            },
        ]

        const emissionsGroupedColumns: ColumnDef<GHGEmissionsReportEntry>[] =
            emissionScopes.map((scope) => ({
                id: scope.id,
                header: scope.header,
                columns: emissionTypes.map((emission) => ({
                    id: `${scope.id}_${emission.type}`,
                    accessorFn: (row: GHGEmissionsReportEntry) =>
                        getEmissionNumericValue(
                            row[scope.id as EmissionScopeKey],
                            emission.type
                        ),
                    header: emission.header,
                    enableSorting: true,
                    sortingFn: 'basic',
                    cell: ({ row }) =>
                        getEmissionValue(
                            row.original[scope.id as EmissionScopeKey],
                            emission.type
                        ),
                })),
            }))

        return [
            ...baseColumns,
            {
                id: 'emissionsGroup',
                header: `${t('sustainability.table.emissions')} (${dateRange})`,
                columns: emissionsGroupedColumns,
            },
        ]
    }, [
        t,
        dateRange,
        getEmissionValue,
        getEmissionNumericValue,
        emissionScopes,
    ])

    const table = useReactTable({
        data,
        columns: columns as ColumnDef<unknown>[],
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: { sorting, pagination },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
    })

    const paginationConfig = usePaginationConfig({
        totalItems: data.length,
        recordsPerPage: pagination.pageSize,
        currentPage: pagination.pageIndex + 1,
        onPageChange: (nextPage) => {
            table.setPageIndex(nextPage - 1)
        },
        onRecordsPerPageChange: (value) => {
            const size = parseInt(value as string, 10)
            table.setPageSize(size)
            table.setPageIndex(0)
        },
        siblingCount: 1,
        boundaryCount: 1,
    })

    return (
        <GHGEmissionsTable
            table={table}
            isLoading={isLoading}
            sorting={sorting}
            setSorting={setSorting}
            loadingTitle={t('analytics.common.report_table_loading')}
            pagination={paginationConfig}
        />
    )
}
